import { Directive, ElementRef, HostListener, inject } from '@angular/core';

/**
 * Commits an input's value when Enter is pressed, by blurring it.
 *
 * Pairs with a reactive control declared `updateOn: 'blur'`. That option exists so a figure the
 * advisor is still typing does not drive live recalculation — replacing 50,000 with 500,000
 * should not swing a total through 5, 50 and 500 on the way — but it has one gap: Enter is the
 * other way people say "done", and on its own it commits nothing. The advisor presses it, sees
 * no change, and reasonably concludes the number did not take.
 *
 * Blurring is the whole implementation: the control is already listening for it, so the value
 * commits through exactly the same path a click elsewhere would take. Nothing here knows about
 * forms, and there is nothing to keep in step with the control's own configuration.
 *
 * ⚠️ Only needed on `updateOn: 'blur'` controls. An input using `(change)` already commits on
 * Enter — the browser fires `change` itself — so adding this there would blur for no reason and
 * cost the advisor their place on the form.
 *
 * Standalone so it can be imported directly by standalone components.
 */
@Directive({
  selector: '[appCommitOnEnter]',
  standalone: true,
})
export class CommitOnEnterDirective {
  private readonly el = inject<ElementRef<HTMLInputElement>>(ElementRef);

  @HostListener('keydown.enter', ['$event'])
  onEnter(event: Event): void {
    // Enter in a lone input can trigger implicit form submission; these inputs sit outside a
    // <form> today, but the wizard is a modal full of controls and that is not a guarantee
    // worth resting on.
    event.preventDefault();
    this.el.nativeElement.blur();
  }
}
